/**
 * Zona horaria oficial del negocio. No depender del dispositivo del usuario.
 * Costa Rica no observa DST, offset fijo UTC-6.
 * Única fuente de verdad para todo cálculo de día/hora en el frontend.
 */
export const CR_TIMEZONE = 'America/Costa_Rica';

/**
 * Devuelve la fecha del día operativo en formato YYYY-MM-DD.
 *
 * Fuerza America/Costa_Rica: sin esto se usaba la zona del dispositivo, así que
 * una tablet/celular mal configurado (o en UTC) devolvía el día siguiente a
 * partir de las 6:00 p.m. CR y desalineaba el POS del backend.
 */
export function fechaLocalYMD(date: string | Date = new Date()): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleDateString('sv-SE', { timeZone: CR_TIMEZONE });
}

/**
 * Formatea una cadena ISO/datetime como hora legible en Costa Rica (HH:MM).
 * Fuerza timeZone 'America/Costa_Rica' para evitar desfases por UTC naive.
 */
export function formatTimeCR(date: string | Date): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleTimeString('es-CR', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'America/Costa_Rica',
  });
}

/**
 * Formatea una cadena ISO/datetime como fecha legible en Costa Rica.
 * Fuerza timeZone 'America/Costa_Rica' para evitar desfases por UTC naive.
 */
export function formatDateCR(date: string | Date): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleDateString('es-CR', {
    timeZone: 'America/Costa_Rica',
  });
}

/**
 * Devuelve el día operativo en formato YYYY-MM-DD.
 * El día operativo es la fecha calendario Costa Rica (00:00–23:59).
 * Alineado con backend: dia_operativo() = fecha CR pura.
 */
export function diaOperativo(): string {
  return fechaLocalYMD(new Date());
}

/**
 * Parsea un ISO date string del backend. Si viene naive (sin Z ni offset),
 * lo trata como UTC explícito — el backend guarda en UTC con func.now()
 * y Pydantic serializa naive. Sin esto, JS lo interpreta como hora local
 * y produce desfases (ej: +6h en Costa Rica UTC-6).
 *
 * Ejemplos:
 *   "2026-05-11T20:41:19"        → trata como 20:41 UTC → 14:41 CR ✅
 *   "2026-05-11T20:41:19Z"       → ya tiene Z, sin cambio  ✅
 *   "2026-05-11T20:41:19-06:00"  → ya tiene offset, sin cambio ✅
 */
export function parseBackendDate(raw: string): Date {
  if (!raw) return new Date();
  return /Z$|[+-]\d{2}:\d{2}$/.test(raw) ? new Date(raw) : new Date(raw + 'Z');
}

/**
 * Formatea hora HH:MM:SS en 24h forzando zona horaria de Costa Rica,
 * independiente de la configuración del dispositivo.
 */
export function formatHoraCR(d: Date): string {
  return d.toLocaleTimeString('es-CR', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
    timeZone: CR_TIMEZONE,
  });
}

/**
 * Formatea fecha DD/MM/YYYY forzando zona horaria de Costa Rica.
 */
export function formatFechaCR(d: Date): string {
  return d.toLocaleDateString('es-CR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: CR_TIMEZONE,
  });
}
